import React, { useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import {
  BoltIcon,
  CogIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PlusCircleIcon,
  RocketLaunchIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import ContactForm from '../organisms/ContactForm'

const AtsIntegrationTemplate = () => {
  const { t } = useTranslation('atsIntegration')
  const [sent, setSent] = useState(false)

  const atsLogos = [
    { name: 'Lever', src: '/next-images/ats/lever.png' },
    { name: 'Recruitee', src: '/next-images/ats/recruitee.png' },
    { name: 'Greenhouse', src: '/next-images/ats/greenhouse.png' },
    { name: 'Workday', src: '/next-images/ats/workday.png' },
    { name: 'Traffit', src: '/next-images/ats/traffit.png' },
    { name: 'Workable', src: '/next-images/ats/workable.png' },
    { name: 'Ashby', src: '/next-images/ats/ashby.png' },
  ]

  const steps = [
    {
      icon: MagnifyingGlassIcon,
      title: t('step1Title'),
      description: t('step1Description'),
    },
    {
      icon: CpuChipIcon,
      title: t('step2Title'),
      description: t('step2Description'),
    },
    {
      icon: RocketLaunchIcon,
      title: t('step3Title'),
      description: t('step3Description'),
    },
  ]

  const benefits = [
    {
      icon: CogIcon,
      title: t('benefit1Title'),
      description: t('benefit1Description'),
    },
    {
      icon: CurrencyDollarIcon,
      title: t('benefit2Title'),
      description: t('benefit2Description'),
    },
    {
      icon: UserGroupIcon,
      title: t('benefit3Title'),
      description: t('benefit3Description'),
    },
    {
      icon: ArrowPathIcon,
      title: t('benefit4Title'),
      description: t('benefit4Description'),
    },
  ]

  const scrollToContact = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const contactSection = document.getElementById('contact')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in-out forwards;
          opacity: 0;
        }
      `}</style>

      {/* Hero Section */}
      <section className='py-20 md:py-32 bg-gradient-to-b from-action-100 to-gray-bg'>
        <div className='container mx-auto px-6 text-center'>
          <div className='max-w-3xl mx-auto'>
            <div className='w-20 h-20 mx-auto bg-action-200 rounded-full flex items-center justify-center mb-6'>
              <BoltIcon className='w-10 h-10 text-action' />
            </div>
            <h1 className='text-4xl md:text-7xl font-bold text-gray-800 mb-6 leading-tight animate-fade-in'>
              {t('heroTitle')}
            </h1>
            <p
              className='text-lg md:text-xl text-gray-600 mb-10 animate-fade-in leading-relaxed'
              style={{ animationDelay: '0.2s' }}
            >
              {t('heroSubtitle')}
            </p>
            <a
              href='#contact'
              onClick={scrollToContact}
              className='bg-primary text-white px-10 py-5 rounded-full inline-block text-xl font-semibold duration-300 hover:bg-primary-hover transform hover:scale-105 shadow-xl animate-fade-in'
              style={{ animationDelay: '0.4s' }}
            >
              {t('heroCta')}
            </a>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className='py-20 bg-white'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-2xl mx-auto mb-16'>
            <h2 className='text-4xl md:text-5xl font-bold text-gray-800 leading-tight'>
              {t('howItWorksTitle')}
            </h2>
            <p className='text-gray-600 mt-6 text-lg leading-relaxed'>
              {t('howItWorksSubtitle')}
            </p>
          </div>
          <div className='grid md:grid-cols-3 gap-8 md:gap-6 text-center max-w-5xl mx-auto relative'>
            {/* Connecting line (visible on md+) */}
            <div className='hidden md:block absolute top-24 left-1/6 right-1/6 h-0.5 bg-action-200' />

            {steps.map((step, index) => (
              <div
                key={index}
                className='bg-gray-bg p-8 rounded-xl shadow-lg animate-fade-in relative'
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className='bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 mt-4 shadow-md'>
                  <step.icon className='w-10 h-10 text-action' />
                </div>
                <h3 className='text-2xl font-bold text-gray-800 mb-3'>
                  {step.title}
                </h3>
                <p className='text-gray-600 leading-relaxed'>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className='py-20 bg-gray-bg'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-16'>
            <h2 className='text-4xl md:text-5xl font-bold text-gray-800 leading-tight'>
              {t('benefitsTitle')}
            </h2>
          </div>
          <div className='grid md:grid-cols-2 gap-10 md:gap-12 max-w-4xl mx-auto'>
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className='flex items-start space-x-5 p-6 rounded-xl bg-white hover:shadow-md transition-shadow duration-300 animate-fade-in'
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className='flex-shrink-0'>
                  <div className='w-14 h-14 bg-action-100 rounded-xl flex items-center justify-center'>
                    <benefit.icon className='w-7 h-7 text-action' />
                  </div>
                </div>
                <div>
                  <h3 className='text-2xl font-bold text-gray-800 mb-2'>
                    {benefit.title}
                  </h3>
                  <p className='text-gray-600 leading-relaxed'>
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Systems Section */}
      <section className='py-20 bg-white'>
        <div className='container mx-auto px-6'>
          <div className='text-center mx-auto'>
            <h2 className='text-4xl md:text-5xl font-bold text-gray-800 leading-tight'>
              {t('supportedAtsTitle')}
            </h2>
            <p className='text-gray-600 mt-6 text-lg leading-relaxed'>
              {t('supportedAtsSubtitle')}
            </p>
          </div>
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-14 max-w-4xl mx-auto'>
            {atsLogos.map((logo, index) => (
              <div
                key={index}
                className='bg-gray-bg rounded-xl p-6 hover:shadow-md transition-shadow duration-300 flex items-center justify-center aspect-[1/1]'
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className='object-contain w-full'
                />
              </div>
            ))}
            <a
              href='#contact'
              onClick={scrollToContact}
              className='bg-gray-bg rounded-xl p-6 hover:shadow-md transition-all duration-300 flex items-center justify-center aspect-[1/1] group cursor-pointer'
            >
              <div className='flex flex-col items-center'>
                <PlusCircleIcon className='h-8 w-8 text-gray-400 group-hover:text-primary transition-colors duration-300' />
                <span className='text-sm text-gray-400 mt-2 group-hover:text-primary transition-colors duration-300'>
                  {t('addYourAts')}
                </span>
              </div>
            </a>
          </div>
          <div className='mx-auto text-center mt-10'>
            <p className='text-xs text-gray-400'>{t('logoDisclaimer')}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id='contact' className='bg-action py-20'>
        <div className='container mx-auto px-6'>
          <h2 className='text-4xl md:text-5xl font-bold mb-6 text-white text-center leading-tight'>
            {t('ctaTitle')}
          </h2>
          <p className='text-lg max-w-2xl mx-auto mb-8 text-white text-center opacity-90 leading-relaxed'>
            {t('ctaSubtitle')}
          </p>
          <ContactForm onSuccess={() => setSent(true)} />
        </div>
      </section>

      {/* Success Modal */}
      {sent && (
        <div className='fixed inset-0 w-full h-screen bg-black bg-opacity-60 z-50 flex justify-center items-center p-4'>
          <div className='bg-white rounded-xl w-full max-w-md py-12 px-8 flex flex-col justify-center items-center text-center shadow-2xl'>
            <div className='w-24 h-24 bg-action-100 rounded-full flex items-center justify-center mb-6'>
              <PaperAirplaneIcon className='w-12 h-12 text-action' />
            </div>
            <p className='mb-4 font-bold text-2xl text-gray-800'>
              {t('modalTitle')}
            </p>
            <p className='text-gray-600 mb-8 leading-relaxed'>
              {t('modalDescription')}
            </p>
            <button
              type='button'
              className='bg-primary text-white px-8 py-4 rounded-full text-lg font-semibold duration-300 hover:bg-primary-hover'
              onClick={() => setSent(false)}
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default AtsIntegrationTemplate
